import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { FileText, Loader2, Plus, Trash2, ChevronLeft, ChevronRight, DollarSign } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Import des données de référence pour les labels
import { vehicleTypes, merchandiseTypes } from '../mockData';
import { logger } from '../utils/logger';
import { applyHiddenTaxToItems, HIDDEN_TAX_RATE } from '../lib/hiddenTax';

// Mapping pour les valeurs legacy de type de véhicule
const LEGACY_VEHICLE_MAPPING = {
  'camion_bache': 'Camion Bâché',
  'camion_plateau': 'Camion Plateau',
  'camion_10t': 'Camion 10T',
  'camion_20t': 'Camion 20T',
  'camion_5t': 'Camion 5T',
  'camion_refrigere': 'Camion Réfrigéré',
  'semi-remorque': 'Semi-remorque',
  'semi_remorque': 'Semi-remorque',
  'camionnette_frigorifique': 'Camionnette Frigorifique'
};

// Helper functions pour récupérer les labels corrects
const getVehicleTypeLabel = (vehicleType, vehicleTypeOther) => {
  if (!vehicleType) return 'Camion';
  
  // Si "autre" est sélectionné avec une valeur personnalisée
  if (vehicleType === 'autre') {
    if (vehicleTypeOther) {
      // Vérifier si vehicleTypeOther est une clé legacy
      if (LEGACY_VEHICLE_MAPPING[vehicleTypeOther]) {
        return LEGACY_VEHICLE_MAPPING[vehicleTypeOther];
      }
      return vehicleTypeOther;
    }
    return 'Autre véhicule'; // Default si "autre" sans précision
  }
  
  // Chercher dans les types standards
  const found = vehicleTypes.find(v => v.id === vehicleType);
  if (found) return found.name;
  
  // Chercher dans le mapping legacy
  if (LEGACY_VEHICLE_MAPPING[vehicleType]) {
    return LEGACY_VEHICLE_MAPPING[vehicleType];
  }
  
  // Formater la valeur brute en label lisible
  return vehicleType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const getMerchandiseTypeLabel = (merchandiseType, merchandiseTypeOther) => {
  if (!merchandiseType) return 'Marchandises diverses';
  
  // Si "autre" est sélectionné avec une valeur personnalisée
  if (merchandiseType === 'autre') {
    if (merchandiseTypeOther) {
      return merchandiseTypeOther;
    }
    return 'Autre marchandise'; // Default si "autre" sans précision
  }
  
  // Chercher dans les types standards
  const found = merchandiseTypes.find(m => m.value === merchandiseType);
  if (found) {
    // Remove emoji from label for cleaner display
    return found.label.replace(/^[^\w\s]+\s*/, '').trim();
  }
  
  // Formater la valeur brute en label lisible
  return merchandiseType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const CreateDetailedQuoteModal = ({ open, onClose, operation, onSuccess, isRevision = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [revisionMessage, setRevisionMessage] = useState('');
  
  // Step 1: Quote Items
  const [quoteItems, setQuoteItems] = useState([{
    designation: '',
    poids: 0,
    unite_payante: 'Camion',
    quantite: 1,
    prix_unitaire_ht: 0,
    montant_total_ht: 0,
    commentaire: ''
  }]);

  // Step 2: Insurance & Taxes
  const [insuranceIncluded, setInsuranceIncluded] = useState(false);
  const [merchandiseValue, setMerchandiseValue] = useState(0);
  const [merchandiseType, setMerchandiseType] = useState('diverse');
  const [coverageZone, setCoverageZone] = useState('CI');
  const [insuranceRate, setInsuranceRate] = useState(0);
  const [applyTVA, setApplyTVA] = useState(true);
  const [tauxTVA, setTauxTVA] = useState(18);
  const [applyHiddenTax, setApplyHiddenTax] = useState(false);
  // Politique de facturation globale (régime d'imposition + taxe 2 %) — Paramètres → FNE
  const [billingPolicy, setBillingPolicy] = useState(null);
  const [quoteNotes, setQuoteNotes] = useState('');
  
  // Step 3: Configurable text zones
  const [quoteIncluded, setQuoteIncluded] = useState('');
  const [quoteExcluded, setQuoteExcluded] = useState('');
  const [quoteGeneralNote, setQuoteGeneralNote] = useState('');
  const [quoteTermsConditions, setQuoteTermsConditions] = useState('');
  const [quoteImmobilizationNote, setQuoteImmobilizationNote] = useState('');
  
  // New: Cancellation policy
  const [quoteCancellationPolicy, setQuoteCancellationPolicy] = useState('');
  
  // New: Immobilization fee configuration
  const [immobilizationFee, setImmobilizationFee] = useState(100000); // 100,000 FCFA par défaut
  const [immobilizationFranchise, setImmobilizationFranchise] = useState(24); // 24h franchise par défaut
  const [immobilizationFranchiseUnit, setImmobilizationFranchiseUnit] = useState('heures'); // 'heures' ou 'jours'
  const [includeLoadingAssistance, setIncludeLoadingAssistance] = useState(false);
  const [loadingAssistanceFee, setLoadingAssistanceFee] = useState(50000);

  // Calculations
  const [montantTotalHT, setMontantTotalHT] = useState(0);
  const [insuranceAmount, setInsuranceAmount] = useState(0);
  const [montantTVA, setMontantTVA] = useState(0);
  const [montantTotalTTC, setMontantTotalTTC] = useState(0);

  const totalSteps = 4;
  
  // Helper function to get franchise label
  const getFranchiseLabel = () => {
    return `Après ${immobilizationFranchise}${immobilizationFranchiseUnit === 'heures' ? 'h' : 'j'} de franchise`;
  };
  
  // Predefined service items - dynamically updated based on configuration
  const predefinedServices = [
    { 
      id: 'immobilization', 
      label: 'Frais d\'immobilisation', 
      defaultPrice: immobilizationFee,  // Uses current state value,
      unit: 'Jour',
      comment: getFranchiseLabel()
    },
    { 
      id: 'loading_help', 
      label: 'Aide au chargement', 
      defaultPrice: loadingAssistanceFee, 
      unit: 'Forfait',
      comment: 'Main d\'œuvre chargement'
    },
    { 
      id: 'unloading_help', 
      label: 'Aide au déchargement', 
      defaultPrice: loadingAssistanceFee, 
      unit: 'Forfait',
      comment: 'Main d\'œuvre déchargement'
    },
    { 
      id: 'extra_trip', 
      label: 'Voyage supplémentaire', 
      defaultPrice: 0, 
      unit: 'Forfait',
      comment: 'Suite refus réception'
    }
  ];
  
  // Generate dynamic "Inclus dans l'offre" based on operation
  const generateIncludedText = () => {
    if (!operation) return '';
    const lines = [];
    lines.push(`• Livraison de ${operation.departure || 'départ'} vers ${operation.arrival || 'arrivée'}`);
    if (operation.weight) lines.push(`• Poids : ${operation.weight} Tonnes`);
    const merchLabel = getMerchandiseTypeLabel(operation.merchandiseType, operation.merchandiseTypeOther);
    if (merchLabel && merchLabel !== 'Marchandises diverses') {
      lines.push(`• Marchandises : ${merchLabel}`);
    }
    const vehicleLabel = getVehicleTypeLabel(operation.vehicleType, operation.vehicleTypeOther);
    if (vehicleLabel) lines.push(`• Véhicule : ${vehicleLabel}`);
    if (operation.numberOfTrucks > 1) lines.push(`• Nombre de camions : ${operation.numberOfTrucks}`);
    // Insurance included reference
    if (insuranceIncluded) {
      const valeurStr = merchandiseValue > 0 ? ` - Valeur déclarée : ${merchandiseValue.toLocaleString('fr-FR')} XOF` : '';
      lines.push(`• Assurance marchandise incluse - La police d'assurance sera transmise en complément de la facture${valeurStr}`);
    }
    return lines.join('\n');
  };
  
  // Generate dynamic "Exclus de l'offre" based on settings
  const generateExcludedText = () => {
    const lines = [];
    const franchiseUnit = immobilizationFranchiseUnit === 'heures' ? 'h' : ' jour(s)';
    lines.push('• Les charges liées à une marchandise dangereuse');
    // Insurance disclaimer based on toggle state
    if (insuranceIncluded) {
      // Insurance is included - no exclusion needed
    } else {
      lines.push('• Assurance marchandise non incluse - Baobab Connect décline toute responsabilité en cas de dommage, perte ou vol de la marchandise transportée');
    }
    lines.push(`• Franchise immobilisation camion après chargement : ${immobilizationFranchise}${franchiseUnit}`);
    lines.push(`• Frais d'immobilisation au-delà de la franchise : ${immobilizationFee.toLocaleString('fr-FR')} FCFA/Jour/Camion`);
    lines.push('• Voyage supplémentaire suite refus de réception : Facturation selon forfait');
    lines.push('• Sécurisation des convois à la charge du client');
    lines.push('• Autres frais non listés mais induits par l\'opération en accord avec le client');
    return lines.join('\n');
  };
  
  // Generate dynamic immobilization note
  const generateImmobilizationNote = () => {
    const franchiseUnit = immobilizationFranchiseUnit === 'heures' ? 'h' : ' jour(s)';
    return `Au-delà de ${immobilizationFranchise}${franchiseUnit} d'immobilisation du véhicule, une pénalité de ${immobilizationFee.toLocaleString('fr-FR')} FCFA/Jour/Camion vous sera facturée.`;
  };
  
  // Generate default general note
  const generateGeneralNote = () => {
    return `Conditions et prise en charges des convois :
• Le lieu d'enlèvement et de livraison doivent être accessibles au transporteur sans contrainte ni risque particulier
• La prise en charge est matérialisée par la signature conjointe du document de transport
• La sécurisation du trajet relève de la responsabilité du donneur d'ordre
• Le planning des opérations doit être transmis 48H avant
• Le chargement par camion et par voyage doit être communiqué avec précision

Réserves et conditions de paiement :
• Cette cotation est régie par les CGV de BAOBAB CONNECT
• Aucune responsabilité supportée concernant la qualité du produit
• Les tarifs sont établis suivant les conditions économiques en vigueur`;
  };
  
  // Generate default cancellation policy text (Paragraphe F)
  const generateCancellationPolicy = () => {
    return `Toute annulation ou report d'une opération confirmée doit être notifié par écrit et n'est pris en compte qu'après confirmation par Baobab Connect. Le Client reconnaît qu'une annulation génère des coûts opérationnels (réservation de capacité, mobilisation du chauffeur et du véhicule, coordination terrain, déplacement éventuel). En conséquence, toute annulation d'une opération confirmée, notamment lorsque le véhicule/chauffeur est déjà assigné, en route ou présenté sur site, entraîne des frais d'annulation et peut donner lieu à la facturation partielle ou totale de la prestation, ainsi qu'au remboursement de tout frais déjà engagé (carburant, péages, manutention, immobilisation, etc.), conformément aux conditions particulières indiquées sur le présent devis.`;
  };

  // Build smart designation with stops
  const buildDesignation = (op) => {
    const shorten = (addr) => {
      if (!addr) return '?';
      const noCountry = addr.replace(/,?\s*Côte d'Ivoire$/i, '').replace(/,?\s*Ivory Coast$/i, '');
      const parts = noCountry.split(',').map(p => p.trim()).filter(Boolean);
      if (parts.length <= 1) return parts[0] || addr;
      const meaningful = parts.filter(p => !['Abidjan', 'abidjan'].includes(p.trim()));
      return meaningful[0] || parts[0];
    };

    const dep = shorten(op.departure);
    const arr = shorten(op.arrival);
    const stops = op.stops || op.etapes || op.intermediateStops || [];

    let route;
    if (stops.length > 0) {
      const stopNames = stops.map(s => shorten(s.address || s.name || ''));
      route = [dep, ...stopNames, arr].join(' → ');
    } else {
      route = `${dep} → ${arr}`;
    }

    const typeLabels = {
      'transport_marchandises': 'Transport marchandises',
      'demenagement': 'Déménagement',
      'location_vehicule': 'Location véhicule',
      'transit': 'Transit',
      'entreposage': 'Entreposage',
    };
    const typeLine = typeLabels[op.operationType] || op.operationType || 'Transport';
    const dateLine = op.deliveryDate ? new Date(op.deliveryDate).toLocaleDateString('fr-FR') : '';

    return `Transport ${route}${dateLine ? `\n${typeLine} • ${dateLine}` : ''}`;
  };

  // Add predefined service item
  const addPredefinedService = (service) => {
    const newItem = {
      designation: service.label,
      poids: 0,
      unite_payante: service.unit,
      quantite: 1,
      prix_unitaire_ht: service.defaultPrice,
      montant_total_ht: service.defaultPrice,
      commentaire: service.comment
    };
    setQuoteItems([...quoteItems, newItem]);
  };

  // Initialize data ONLY when modal opens (not on every operation change)
  useEffect(() => {
    if (open && operation && !initialized) {
      // Récupérer le label correct du type de véhicule
      const vehicleLabel = getVehicleTypeLabel(operation.vehicleType, operation.vehicleTypeOther);
      
      // Récupérer le label correct du type de marchandise
      const merchandiseLabel = getMerchandiseTypeLabel(operation.merchandiseType, operation.merchandiseTypeOther);
      
      // Récupérer la quantité (numberOfTrucks) depuis l'opération
      const quantity = operation.numberOfTrucks || 1;
      
      logger.debug('Initializing quote from operation:', {
        vehicleType: operation.vehicleType,
        vehicleTypeOther: operation.vehicleTypeOther,
        vehicleLabel,
        merchandiseType: operation.merchandiseType,
        merchandiseTypeOther: operation.merchandiseTypeOther,
        merchandiseLabel,
        numberOfTrucks: operation.numberOfTrucks,
        quantity,
        weight: operation.weight,
        // Existing quote data
        quoteItems: operation.quoteItems,
        immobilizationFee: operation.immobilizationFee,
        immobilizationFranchise: operation.immobilizationFranchise,
        immobilizationFranchiseUnit: operation.immobilizationFranchiseUnit
      });
      
      // Load existing quote items if available (for quote regeneration)
      if (operation.quoteItems && operation.quoteItems.length > 0) {
        setQuoteItems(operation.quoteItems.map(item => {
          // Toujours éditer sur le prix de BASE (idempotence taxe cachée)
          const baseUnit = item.prix_unitaire_base_ht ?? item.prix_unitaire_ht ?? item.unitPrice ?? 0;
          const qte = item.quantite || item.quantity || 1;
          return {
            designation: item.designation || item.description || '',
            poids: item.poids || 0,
            unite_payante: item.unite_payante || item.unit || 'Camion',
            quantite: qte,
            prix_unitaire_ht: baseUnit,
            montant_total_ht: qte * baseUnit,
            commentaire: item.commentaire || ''
          };
        }));
      } else {
        // PM Spec 25/04/2026 §4.1 — Pré-remplir depuis estimatedPrice (Tarif Client HT)
        // Si estimatedPrice existe, on injecte une ligne unique "Transport ..." avec PU calculé.
        // La dernière quantité absorbe l'arrondi au FCFA pour garantir total == estimatedPrice.
        const baseEstimatedPrice = Number(operation.estimatedPrice) || 0;
        const isPrefilled = baseEstimatedPrice > 0;
        const unitPrice = isPrefilled && quantity > 0
          ? Math.floor(baseEstimatedPrice / quantity)
          : 0;
        // Arrondi : si PU * qty != estimatedPrice, on ajoute le delta au PU (absorbé sur la dernière unité).
        // Exemple : 100000 / 3 = 33333.33 → PU = 33333, total = 99999. Delta = 1.
        // On stocke le PU au plus juste ; l'écart résiduel (< quantite FCFA) est accepté par design.
        const totalComputed = unitPrice * quantity;
        const residual = baseEstimatedPrice - totalComputed; // ≥ 0 par Math.floor
        const finalTotalHT = isPrefilled ? baseEstimatedPrice : 0;

        setQuoteItems([{
          designation: buildDesignation(operation),
          poids: operation.weight || 0,
          unite_payante: vehicleLabel,
          quantite: quantity,
          prix_unitaire_ht: unitPrice,
          montant_total_ht: finalTotalHT,
          commentaire: isPrefilled && residual > 0
            ? `Tarif client pré-rempli (ajustement arrondi +${residual.toLocaleString('fr-FR')} FCFA inclus dans le total)`
            : (isPrefilled ? 'Tarif client pré-rempli depuis l\'opération. Modifiable.' : ''),
          _prefilledFromEstimatedPrice: isPrefilled  // flag interne pour badge UI
        }]);
      }
      
      setMerchandiseValue(operation.merchandiseValue || 0);
      setInsuranceIncluded(!!(operation.needsInsurance && operation.merchandiseValue > 0));
      setMerchandiseType(merchandiseLabel);
      setCoverageZone(operation.intraInterLand === 'INTERLAND' ? 'CEDEAO' : 'CI');
      
      // Load immobilization fee configuration if available
      if (operation.immobilizationFee) {
        setImmobilizationFee(operation.immobilizationFee);
      }
      if (operation.immobilizationFranchise) {
        setImmobilizationFranchise(operation.immobilizationFranchise);
      }
      if (operation.immobilizationFranchiseUnit) {
        setImmobilizationFranchiseUnit(operation.immobilizationFranchiseUnit);
      }
      
      // Load TVA settings if available
      if (operation.applyTVA !== undefined) {
        setApplyTVA(operation.applyTVA);
      }
      if (operation.tauxTVA) {
        setTauxTVA(operation.tauxTVA);
      }
      setApplyHiddenTax(!!operation.hiddenTax?.applied);
      // Politique globale : régime sans TVA => TVA forcée à non ; défaut taxe 2 %
      axios.get(`${API}/fne/billing-policy`).then(({ data }) => {
        setBillingPolicy(data);
        if (data?.vatBlocked) {
          setApplyTVA(false);
          setTauxTVA(0);
        } else if (operation.applyTVA === undefined && data?.defaultVatApplied) {
          setApplyTVA(true);
          setTauxTVA(data.defaultVatRate || 18);
        }
        if (!operation.hiddenTax) {
          setApplyHiddenTax(!!data?.hiddenTaxEnabled);
        }
      }).catch(() => {});
      if (operation.insuranceRate) {
        setInsuranceRate(operation.insuranceRate);
      }
      
      // Initialize configurable text zones with default values
      setQuoteIncluded(operation.quoteIncluded || '');
      setQuoteExcluded(operation.quoteExcluded || '');
      setQuoteGeneralNote(operation.quoteGeneralNote || '');
      setQuoteTermsConditions(operation.quoteTermsConditions || '');
      setQuoteImmobilizationNote(operation.quoteImmobilizationNote || '');
      setQuoteCancellationPolicy(operation.quoteCancellationPolicy || '');
      setQuoteNotes(operation.quoteNotes || '');
      
      setInitialized(true);
    }
    // Reset initialized flag when modal closes
    if (!open) {
      setInitialized(false);
      setCurrentStep(1);
      // Reset configurable text zones
      setQuoteIncluded('');
      setQuoteExcluded('');
      setQuoteGeneralNote('');
      setQuoteTermsConditions('');
      setQuoteImmobilizationNote('');
      setQuoteCancellationPolicy('');
      // Reset immobilization settings to defaults
      setImmobilizationFee(100000);
      setImmobilizationFranchise(24);
      setImmobilizationFranchiseUnit('heures');
      setInsuranceIncluded(false);
    }
  }, [open, operation, initialized]);

  useEffect(() => {
    calculateTotals();
  }, [quoteItems, insuranceRate, tauxTVA, applyTVA, applyHiddenTax, insuranceIncluded, merchandiseValue]);

  useEffect(() => {
    calculateInsuranceRate();
  }, [merchandiseValue, merchandiseType, coverageZone]);

  const calculateInsuranceRate = () => {
    // Taux selon le document PDF
    let rate = 0;
    
    // Check if merchandise is refrigerated (with safe toLowerCase)
    const merchandiseTypeLower = merchandiseType?.toLowerCase() || '';
    const isRefrigerated = merchandiseTypeLower.includes('réfrigér') || 
                          merchandiseTypeLower.includes('refriger') ||
                          merchandiseTypeLower.includes('froid');
    
    if (coverageZone === 'CI') {
      // Côte d'Ivoire (INTRALAND)
      rate = isRefrigerated ? 0.25 : 0.18;
    } else if (coverageZone === 'CEDEAO') {
      // CEDEAO (INTERLAND)
      rate = isRefrigerated ? 0.30 : 0.25;
    }
    
    setInsuranceRate(rate);
  };

  const calculateTotals = () => {
    // Lignes effectives = prix unitaires avec taxe cachée 2% incorporée (si activée)
    const { items: effItems } = applyHiddenTaxToItems(quoteItems, applyHiddenTax);
    // Calculate total HT from all items
    const totalHT = effItems.reduce((sum, item) => sum + (item.montant_total_ht || 0), 0);
    setMontantTotalHT(totalHT);

    // Calculate insurance based on merchandise value (not transport cost)
    const insurance = insuranceIncluded && merchandiseValue > 0 ? (merchandiseValue * insuranceRate) / 100 : 0;
    setInsuranceAmount(insurance);

    // Calculate TVA on (totalHT + insurance) ONLY if applyTVA is true
    const baseForTVA = totalHT + insurance;
    const tva = applyTVA ? (baseForTVA * tauxTVA) / 100 : 0;
    setMontantTVA(tva);

    // Calculate total TTC
    // If TVA not applied, TTC = HT (as requested by user)
    const totalTTC = baseForTVA + tva;
    setMontantTotalTTC(totalTTC);
  };

  const addQuoteItem = () => {
    setQuoteItems([...quoteItems, {
      designation: '',
      poids: 0,
      unite_payante: 'Camion',
      quantite: 1,
      prix_unitaire_ht: 0,
      montant_total_ht: 0,
      commentaire: ''
    }]);
  };

  const removeQuoteItem = (index) => {
    if (quoteItems.length > 1) {
      setQuoteItems(quoteItems.filter((_, i) => i !== index));
    }
  };

  const updateQuoteItem = (index, field, value) => {
    const newItems = [...quoteItems];
    newItems[index][field] = value;

    // Recalculate montant_total_ht if quantite or prix_unitaire_ht changed
    if (field === 'quantite' || field === 'prix_unitaire_ht') {
      const quantite = parseFloat(newItems[index].quantite) || 0;
      const prix_unitaire = parseFloat(newItems[index].prix_unitaire_ht) || 0;
      newItems[index].montant_total_ht = quantite * prix_unitaire;
    }

    setQuoteItems(newItems);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Validate items
      const hasValidItems = quoteItems.some(item => 
        item.designation && item.quantite > 0 && item.prix_unitaire_ht > 0
      );
      if (!hasValidItems) {
        toast({
          title: "Erreur",
          description: "Veuillez ajouter au moins un item valide avec un tarif",
          variant: "destructive"
        });
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Taxe cachée 2% : lignes effectives (prix unitaires taxés) + métadonnées internes
      const { items: effItems, meta: hiddenTaxMeta } = applyHiddenTaxToItems(quoteItems, applyHiddenTax);
      // Prepare quote data
      // IMPORTANT: estimatedPrice = prix du transport SEUL (montantTotalHT sans assurance)
      // L'assurance est stockée séparément dans insuranceAmount
      const quoteData = {
        quoteItems: effItems,
        montantTotalHT: montantTotalHT,  // Prix transport seul,
        applyTVA: applyTVA,
        tauxTVA: applyTVA ? tauxTVA : 0,
        montantTVA: montantTVA,
        montantTotalTTC: montantTotalTTC,  // Transport + Assurance + TVA,
        insuranceRate: insuranceRate,
        insuranceAmount: insuranceAmount,
        merchandiseValue: merchandiseValue,
        merchandiseType: merchandiseType,
        coverageZone: coverageZone,
        quoteNotes: quoteNotes,
        estimatedPrice: montantTotalHT,  // Prix transport SEUL (sans assurance),
        needsInsurance: insuranceIncluded,  // Toggle assurance du devis,
        merchandiseValue: insuranceIncluded ? merchandiseValue : 0,  // Valeur déclarée si assurance active
        // Taxe globale cachée 2%
        applyHiddenTax: applyHiddenTax,
        hiddenTax: hiddenTaxMeta,
        // Immobilization fee configuration
        immobilizationFee: immobilizationFee,
        immobilizationFranchise: immobilizationFranchise,
        immobilizationFranchiseUnit: immobilizationFranchiseUnit,
        // Configurable text zones
        quoteIncluded: quoteIncluded,
        quoteExcluded: quoteExcluded,
        quoteGeneralNote: quoteGeneralNote,
        quoteTermsConditions: quoteTermsConditions,
        quoteImmobilizationNote: quoteImmobilizationNote,
        quoteCancellationPolicy: quoteCancellationPolicy
      };

      // MODE RÉVISION : Utiliser l'endpoint de révision
      if (isRevision) {
        await axios.post(
          `${API}/transport-requests/${operation.id}/revise-quote`,
          {
            quoteItems: effItems.map(item => ({
              description: item.designation,
              quantity: item.quantite,
              unitPrice: item.prix_unitaire_ht,
              baseUnitPrice: item.prix_unitaire_base_ht,
              unit: item.unite_payante
            })),
            applyTVA: applyTVA,
            tauxTVA: applyTVA ? tauxTVA : 0,
            hiddenTax: hiddenTaxMeta,
            message: revisionMessage || `Devis révisé: ${montantTotalTTC.toLocaleString('fr-FR')} XOF`
          },
          {
            headers: {
              'Authorization': 'Bearer cookie-auth',
              'Content-Type': 'application/json'
            }
          }
        );

        toast({
          title: "✅ Devis révisé",
          description: `Le devis a été révisé à ${montantTotalTTC.toLocaleString('fr-FR')} XOF et renvoyé au client`
        });

        if (onSuccess) {
          onSuccess();
        }
        onClose();
        return;
      }

      // MODE CRÉATION STANDARD
      // 1. Update operation with quote data
      await axios.patch(
        `${API}/transport-requests/${operation.id}`,
        quoteData,
        {
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Content-Type': 'application/json'
          }
        }
      );

      // 2. Handle status transitions automatically
      const currentStatus = operation.status;
      
      // If starting from "demande_enregistree", first transition to "en_attente_de_devis"
      if (currentStatus === 'demande_enregistree') {
        await axios.post(
          `${API}/operation-status/${operation.id}/change`,
          {
            new_status: 'en_attente_de_devis',
            reason: 'Passage en étude pour création du devis',
            validate_requirements: false
          },
          {
            headers: {
              'Authorization': 'Bearer cookie-auth',
              'Content-Type': 'application/json'
            }
          }
        );
      }

      // 3. Now transition to "en_attente_validation_client" (Workflow V2: devis_cree fusionné)
      await axios.post(
        `${API}/operation-status/${operation.id}/change`,
        {
          new_status: 'en_attente_validation_client',
          reason: quoteNotes || `Devis créé avec un montant total de ${montantTotalTTC.toLocaleString('fr-FR')} XOF`,
          validate_requirements: false
        },
        {
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Content-Type': 'application/json'
          }
        }
      );

      toast({
        title: "✅ Devis créé",
        description: `Le devis de ${montantTotalTTC.toLocaleString('fr-FR')} XOF a été créé et envoyé au client`
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating quote:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer le devis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            {/* Client Request Summary */}
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Demande du client</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Trajet:</span>
                    <span className="ml-1 font-medium">{operation?.departure} → {operation?.arrival}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Poids:</span>
                    <span className="ml-1 font-medium">{operation?.weight} T</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Camions:</span>
                    <span className="ml-1 font-medium">{operation?.numberOfTrucks || 1}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type véhicule:</span>
                    <span className="ml-1 font-medium">{getVehicleTypeLabel(operation?.vehicleType, operation?.vehicleTypeOther)}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Marchandise:</span>
                    <span className="ml-1 font-medium">{getMerchandiseTypeLabel(operation?.merchandiseType, operation?.merchandiseTypeOther)}</span>
                  </div>
                  {operation?.needsInsurance && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Assurance demandée:</span>
                      <span className="ml-1 font-medium text-green-700">✓ Oui ({operation?.merchandiseValue?.toLocaleString('fr-FR')} XOF)</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Immobilization Fee Configuration - BEFORE quick add buttons */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardContent className="pt-4">
                <h4 className="text-sm font-semibold text-amber-900 mb-3">⚙️ Configuration Frais d'Immobilisation</h4>
                <p className="text-xs text-amber-700 mb-3">
                  Ces paramètres seront utilisés automatiquement dans les services rapides et les textes du devis
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Montant par jour (FCFA)</Label>
                    <Input
                      type="number"
                      step="10000"
                      value={immobilizationFee}
                      onChange={(e) => setImmobilizationFee(parseInt(e.target.value) || 0)}
                      className="mt-1"
                      data-testid="immobilization-fee-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Franchise</Label>
                    <Input
                      type="number"
                      step="1"
                      value={immobilizationFranchise}
                      onChange={(e) => setImmobilizationFranchise(parseInt(e.target.value) || 24)}
                      className="mt-1"
                      data-testid="immobilization-franchise-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unité franchise</Label>
                    <select
                      value={immobilizationFranchiseUnit}
                      onChange={(e) => setImmobilizationFranchiseUnit(e.target.value)}
                      className="w-full mt-1 p-2 border rounded-md bg-white text-sm"
                      data-testid="immobilization-franchise-unit-select"
                    >
                      <option value="heures">Heures</option>
                      <option value="jours">Jours</option>
                    </select>
                  </div>
                </div>
                <div className="mt-2 p-2 bg-amber-100/50 rounded text-xs text-amber-800">
                  💡 Frais appliqués : <strong>{immobilizationFee.toLocaleString('fr-FR')} FCFA/jour</strong> après <strong>{immobilizationFranchise} {immobilizationFranchiseUnit}</strong> de franchise
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Items du Devis</h3>
              <Button onClick={addQuoteItem} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un item
              </Button>
            </div>
            
            {/* Quick Add Predefined Services */}
            <div className="bg-gray-50 p-3 rounded-lg border">
              <p className="text-xs text-gray-500 mb-2">Ajouter un service rapide :</p>
              <div className="flex flex-wrap gap-2">
                {predefinedServices.map((service) => (
                  <Button
                    key={service.id}
                    onClick={() => addPredefinedService(service)}
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 px-2 border-dashed hover:border-solid hover:bg-green-50 hover:border-green-300 hover:text-green-700"
                    data-testid={`quick-add-${service.id}`}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {service.label}
                    {service.id === 'immobilization' && (
                      <span className="ml-1 text-green-600 font-medium">
                        ({immobilizationFee.toLocaleString('fr-FR')} FCFA)
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            </div>

            {quoteItems.map((item, index) => (
              <Card key={index} className="border-2">
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">Item {index + 1}</span>
                      {item._prefilledFromEstimatedPrice && (
                        <span
                          data-testid={`prefill-badge-${index}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full"
                          title="Ligne pré-remplie à partir du Tarif Client de l'opération. Tous les champs restent modifiables."
                        >
                          <DollarSign className="w-2.5 h-2.5" />
                          Pré-rempli depuis le Tarif Client
                        </span>
                      )}
                    </div>
                    {quoteItems.length > 1 && (
                      <Button
                        onClick={() => removeQuoteItem(index)}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Détail trajet *</Label>
                      <Textarea
                        placeholder="Départ → Arrivée&#10;Date de livraison"
                        value={item.designation}
                        onChange={(e) => updateQuoteItem(index, 'designation', e.target.value)}
                        rows={3}
                        className="min-h-[80px]"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Adresse départ → Adresse arrivée (ligne 1), Date (ligne 2)
                      </p>
                    </div>

                    <div>
                      <Label>Poids (Tonnes)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0"
                        value={item.poids || ''}
                        onChange={(e) => updateQuoteItem(index, 'poids', parseFloat(e.target.value) || 0)}
                      />
                      <p className="text-xs text-gray-500 mt-1">Récupéré du formulaire</p>
                    </div>

                    <div>
                      <Label>Véhicule *</Label>
                      <Input
                        placeholder="Ex: Camion Plateau"
                        value={item.unite_payante}
                        onChange={(e) => updateQuoteItem(index, 'unite_payante', e.target.value)}
                      />
                      <p className="text-xs text-gray-500 mt-1">Type de véhicule</p>
                    </div>

                    <div>
                      <Label>Quantité *</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="1"
                        value={item.quantite || ''}
                        onChange={(e) => updateQuoteItem(index, 'quantite', parseFloat(e.target.value) || 0)}
                      />
                    </div>

                    <div>
                      <Label>Prix Unitaire HT (XOF) *</Label>
                      <Input
                        type="number"
                        step="1000"
                        placeholder="0"
                        value={item.prix_unitaire_ht || ''}
                        onChange={(e) => updateQuoteItem(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Montant Total HT</Label>
                      <div className="text-xl font-bold text-[#1db584]">
                        {item.montant_total_ht.toLocaleString('fr-FR')} XOF
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 mb-4">Assurance & Taxes</h3>

            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-4 space-y-3">
                <div className="p-3 bg-blue-100 rounded-lg mb-3">
                  <p className="text-xs text-blue-900">
                    <strong>ℹ️ Calcul de l'assurance :</strong> L'assurance est calculée sur la valeur déclarée de la marchandise, 
                    pas sur le tarif du transport.
                  </p>
                </div>

                {/* Toggle Assurance */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Inclure l'assurance marchandise</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {operation?.needsInsurance 
                          ? 'Le client a demandé une assurance lors de la création' 
                          : "Le client n'a pas demandé d'assurance à la création"}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="insurance-toggle"
                      onClick={() => setInsuranceIncluded(!insuranceIncluded)}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                        insuranceIncluded ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                        insuranceIncluded ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {insuranceIncluded ? (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg mb-3">
                    <div className="flex items-start gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-green-900 mb-1">
                          Assurance marchandise incluse
                        </p>
                        <p className="text-xs text-green-700">
                          La police d'assurance sera transmise en complément de la facture
                        </p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs text-green-900">Valeur déclarée de la marchandise (XOF)</Label>
                      <Input
                        type="number"
                        step="100000"
                        placeholder="Ex: 30000000"
                        value={merchandiseValue}
                        onChange={(e) => setMerchandiseValue(parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                      {merchandiseValue > 0 && (
                        <p className="text-sm font-semibold text-green-800 mt-1">
                          Valeur: {merchandiseValue.toLocaleString('fr-FR')} XOF
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                    <p className="text-xs text-amber-900">
                      ⚠️ Assurance marchandise non incluse. Activez le toggle ci-dessus si le client souhaite souscrire.
                    </p>
                  </div>
                )}

                {insuranceIncluded && (
                <div className="space-y-3">
                  <div>
                    <Label>Type de marchandise (du formulaire)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={merchandiseType || 'Marchandises diverses'}
                        onChange={(e) => setMerchandiseType(e.target.value)}
                        placeholder="Type de marchandise"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Récupéré du formulaire client. Modifiable si nécessaire.
                    </p>
                  </div>

                  <div>
                    <Label>Zone de couverture</Label>
                    <select
                      value={coverageZone}
                      onChange={(e) => setCoverageZone(e.target.value)}
                      className="w-full p-3 border rounded-lg bg-white"
                    >
                      <option value="CI">INTRALAND (Côte d'Ivoire)</option>
                      <option value="CEDEAO">INTERLAND (CEDEAO / Pays voisins)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {coverageZone === 'CI' 
                        ? 'Transport interne à la Côte d\'Ivoire' 
                        : 'Transport vers/depuis les pays voisins (Mali, Burkina, Ghana, etc.)'}
                    </p>
                  </div>
                </div>
                )}

                {/* Taux d'assurance - visible seulement si assurance activée */}
                {insuranceIncluded && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-green-800 font-semibold">Taux d'assurance</Label>
                      <p className="text-xs text-green-600">
                        Taux par défaut selon la zone. Modifiable si besoin.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="10"
                        value={insuranceRate}
                        onChange={(e) => setInsuranceRate(parseFloat(e.target.value) || 0)}
                        className="w-24 text-right font-semibold text-green-800"
                      />
                      <span className="text-green-800 font-semibold">%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-sm pt-2 border-t border-green-200">
                    <span className="text-gray-700">Montant de l'assurance:</span>
                    <span className="font-bold text-green-800">{insuranceAmount.toLocaleString('fr-FR')} XOF</span>
                  </div>
                  
                  {/* Quick rate buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setInsuranceRate(0.18)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        insuranceRate === 0.18 
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      0.18% (Intra std)
                    </button>
                    <button
                      type="button"
                      onClick={() => setInsuranceRate(0.25)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        insuranceRate === 0.25 
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      0.25% (Inter std)
                    </button>
                    <button
                      type="button"
                      onClick={() => setInsuranceRate(0.30)}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        insuranceRate === 0.30 
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      0.30% (Réfrigéré)
                    </button>
                  </div>
                </div>
                )}

                <div className="border-t pt-3 space-y-3">
                  {/* Option TVA Oui/Non */}
                  <div
                    className={`flex items-center justify-between p-3 border rounded-lg ${billingPolicy?.vatBlocked ? 'bg-gray-100 border-gray-200 opacity-80' : 'bg-amber-50 border-amber-200'}`}
                    data-testid="quote-tva-block"
                  >
                    <div className="flex-1 pr-3">
                      <Label className={`text-base font-semibold ${billingPolicy?.vatBlocked ? 'text-gray-500' : 'text-amber-900'}`}>
                        Appliquer la TVA ?
                        {billingPolicy?.vatBlocked && (
                          <span className="ml-2 text-xs font-normal uppercase tracking-wide text-gray-500">
                            désactivée
                          </span>
                        )}
                      </Label>
                      <p className={`text-xs mt-1 ${billingPolicy?.vatBlocked ? 'text-gray-500' : 'text-amber-700'}`}>
                        {billingPolicy?.vatBlocked
                          ? billingPolicy.message
                          : 'Si Non sélectionné : le montant HT = TTC (pas de TVA appliquée)'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        disabled={billingPolicy?.vatBlocked}
                        onClick={() => setApplyTVA(true)}
                        data-testid="quote-tva-yes"
                        className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          applyTVA
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        disabled={billingPolicy?.vatBlocked}
                        onClick={() => setApplyTVA(false)}
                        data-testid="quote-tva-no"
                        className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          !applyTVA
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>

                  {/* Taux TVA (only if TVA is applied) */}
                  {applyTVA && (
                    <div>
                      <Label>Taux TVA (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={tauxTVA}
                        onChange={(e) => setTauxTVA(parseFloat(e.target.value) || 0)}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Montant TVA: {montantTVA.toLocaleString('fr-FR')} XOF
                      </p>
                    </div>
                  )}

                  {/* Info message when TVA not applied */}
                  {!applyTVA && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-900">
                        ℹ️ <strong>TVA non appliquée</strong> : Le montant HT sera égal au montant TTC
                      </p>
                    </div>
                  )}

                  {/* Taxe globale cachée 2% (invisible au client) */}
                  <div className="flex items-center justify-between p-3 bg-violet-50 border border-violet-200 rounded-lg" data-testid="hidden-tax-block">
                    <div className="flex-1 pr-3">
                      <Label className="text-base font-semibold text-violet-900">
                        Taxe globale cachée ({HIDDEN_TAX_RATE}%)
                      </Label>
                      <p className="text-xs text-violet-700 mt-1">
                        Incorporée dans les prix unitaires (arrondis au 100 FCFA) pour couvrir les frais
                        de règlement. <strong>Invisible pour le client</strong> — aucune ligne de taxe séparée.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        data-testid="hidden-tax-on-btn"
                        onClick={() => setApplyHiddenTax(true)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          applyHiddenTax ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        data-testid="hidden-tax-off-btn"
                        onClick={() => setApplyHiddenTax(false)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          !applyHiddenTax ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        Non
                      </button>
                    </div>
                  </div>
                  {applyHiddenTax && (() => {
                    const { meta } = applyHiddenTaxToItems(quoteItems, true);
                    return (
                      <div className="p-3 bg-violet-50/60 border border-violet-200 rounded-lg text-sm" data-testid="hidden-tax-summary">
                        <div className="flex justify-between text-violet-800">
                          <span>Base réelle HT (hors taxe cachée)</span>
                          <span>{(meta.baseTotalHT || 0).toLocaleString('fr-FR')} XOF</span>
                        </div>
                        <div className="flex justify-between text-violet-900 font-medium">
                          <span>Marge taxe cachée {HIDDEN_TAX_RATE}%</span>
                          <span>+{(meta.taxTotal || 0).toLocaleString('fr-FR')} XOF</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            <div>
              <Label>Notes / Commentaires</Label>
              <Textarea
                placeholder="Ex: Prix incluant péage et assurance marchandise..."
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 mb-2">Conditions & Notes du Devis</h3>
            <p className="text-xs text-gray-500 mb-4">
              💡 Astuce : Cliquez sur "Générer" pour pré-remplir automatiquement avec les données de l'opération
            </p>
            
            {/* Reminder of immobilization settings */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                ⚙️ <strong>Rappel paramètres frais d'immobilisation</strong> (configurés à l'étape 1) : 
                <span className="ml-1 font-semibold">{immobilizationFee.toLocaleString('fr-FR')} FCFA/jour</span> après 
                <span className="ml-1 font-semibold">{immobilizationFranchise} {immobilizationFranchiseUnit}</span> de franchise
              </p>
            </div>
            
            {/* A. Inclus dans l'offre */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-green-700 font-semibold">A. Inclus dans l'offre</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50"
                  onClick={() => setQuoteIncluded(generateIncludedText())}
                >
                  ✨ Générer
                </Button>
              </div>
              <Textarea
                placeholder={generateIncludedText()}
                value={quoteIncluded}
                onChange={(e) => setQuoteIncluded(e.target.value)}
                rows={4}
                className="border-green-200 focus:border-green-400"
              />
              <p className="text-xs text-gray-500">Pré-rempli avec les infos de l'opération</p>
            </div>
            
            {/* B. Exclus de l'offre */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-orange-700 font-semibold">B. Exclus de l'offre (en sus le cas échéant)</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-orange-700 border-orange-300 hover:bg-orange-50"
                  onClick={() => setQuoteExcluded(generateExcludedText())}
                >
                  ✨ Générer
                </Button>
              </div>
              <Textarea
                placeholder={generateExcludedText()}
                value={quoteExcluded}
                onChange={(e) => setQuoteExcluded(e.target.value)}
                rows={5}
                className="border-orange-200 focus:border-orange-400"
              />
              <p className="text-xs text-gray-500">Inclut automatiquement les frais d'immobilisation configurés</p>
            </div>
            
            {/* D. Message frais d'immobilisation */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-amber-700 font-semibold">D. ⚠️ Information Frais d'Immobilisation</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => setQuoteImmobilizationNote(generateImmobilizationNote())}
                >
                  ✨ Générer
                </Button>
              </div>
              <Textarea
                placeholder={generateImmobilizationNote()}
                value={quoteImmobilizationNote}
                onChange={(e) => setQuoteImmobilizationNote(e.target.value)}
                rows={2}
                className="border-amber-200 focus:border-amber-400 bg-amber-50/50"
              />
              <p className="text-xs text-amber-600">Ce message apparaîtra en évidence sur le devis</p>
            </div>
            
            {/* E. Note Générale */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-blue-700 font-semibold">E. Note Générale</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={() => setQuoteGeneralNote(generateGeneralNote())}
                >
                  ✨ Générer
                </Button>
              </div>
              <Textarea
                placeholder={generateGeneralNote()}
                value={quoteGeneralNote}
                onChange={(e) => setQuoteGeneralNote(e.target.value)}
                rows={6}
                className="border-blue-200 focus:border-blue-400"
              />
            </div>
            
            {/* F. Annulation / Report */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-red-700 font-semibold">F. Annulation / Report</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
                  onClick={() => setQuoteCancellationPolicy(generateCancellationPolicy())}
                  data-testid="generate-cancellation-policy-btn"
                >
                  Générer
                </Button>
              </div>
              <Textarea
                placeholder={generateCancellationPolicy()}
                value={quoteCancellationPolicy}
                onChange={(e) => setQuoteCancellationPolicy(e.target.value)}
                rows={5}
                className="border-red-200 focus:border-red-400 bg-red-50/30"
                data-testid="cancellation-policy-textarea"
              />
              <p className="text-xs text-red-600">Conditions d'annulation standard de Baobab Connect</p>
            </div>
            
            {/* Conditions générales de vente */}
            <div className="space-y-2">
              <Label className="text-purple-700 font-semibold">Conditions Générales de Vente (optionnel)</Label>
              <Textarea
                placeholder="Cette cotation est régie par les conditions générales de vente (CGV) de BAOBAB CONNECT..."
                value={quoteTermsConditions}
                onChange={(e) => setQuoteTermsConditions(e.target.value)}
                rows={3}
                className="border-purple-200 focus:border-purple-400"
              />
            </div>
            
            {/* Bouton tout générer */}
            <div className="pt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed border-gray-400 text-gray-600 hover:bg-gray-50"
                onClick={() => {
                  setQuoteIncluded(generateIncludedText());
                  setQuoteExcluded(generateExcludedText());
                  setQuoteImmobilizationNote(generateImmobilizationNote());
                  setQuoteGeneralNote(generateGeneralNote());
                  setQuoteCancellationPolicy(generateCancellationPolicy());
                }}
              >
                ✨ Générer toutes les sections automatiquement
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 mb-4">Récapitulatif du Devis</h3>

            {/* Items Summary */}
            <Card>
              <CardContent className="pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Items</h4>
                {quoteItems.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm py-2 border-b last:border-0">
                    <span className="text-gray-600">{item.designation}</span>
                    <span className="font-medium">{item.montant_total_ht.toLocaleString('fr-FR')} XOF</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Totals */}
            <Card className="border-purple-200 bg-purple-50/30">
              <CardContent className="pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Montant Total HT</span>
                  <span className="font-semibold">{montantTotalHT.toLocaleString('fr-FR')} XOF</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Assurance ({insuranceRate}%)</span>
                  <span className="font-semibold">{insuranceAmount.toLocaleString('fr-FR')} XOF</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Sous-total</span>
                  <span className="font-semibold">{(montantTotalHT + insuranceAmount).toLocaleString('fr-FR')} XOF</span>
                </div>
                
                {/* TVA line - only show if applied */}
                {applyTVA ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">TVA ({tauxTVA}%)</span>
                    <span className="font-semibold">{montantTVA.toLocaleString('fr-FR')} XOF</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">TVA</span>
                    <span className="font-semibold text-red-600">Non appliquée</span>
                  </div>
                )}
                
                <Separator className="my-2" />
                <div className="flex justify-between text-lg">
                  <span className="font-bold text-gray-900">Montant Total TTC</span>
                  <span className="font-bold text-[#1db584]">{montantTotalTTC.toLocaleString('fr-FR')} XOF</span>
                </div>
                
                {/* Info message if TVA not applied */}
                {!applyTVA && (
                  <div className="text-xs text-blue-700 bg-blue-50 p-2 rounded mt-2">
                    ℹ️ TVA non appliquée : HT = TTC
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Summary of configured text zones */}
            {(quoteIncluded || quoteExcluded || quoteImmobilizationNote || quoteGeneralNote || quoteTermsConditions || quoteCancellationPolicy) && (
              <Card className="border-gray-200">
                <CardContent className="pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Zones de texte personnalisées</h4>
                  <div className="text-xs text-gray-500 space-y-1">
                    {quoteIncluded && <p>✓ Inclus dans l'offre (personnalisé)</p>}
                    {quoteExcluded && <p>✓ Exclus de l'offre (personnalisé)</p>}
                    {quoteImmobilizationNote && <p>✓ Message frais d'immobilisation</p>}
                    {quoteGeneralNote && <p>✓ Note générale (personnalisée)</p>}
                    {quoteCancellationPolicy && <p>✓ Conditions d'annulation (Paragraphe F)</p>}
                    {quoteTermsConditions && <p>✓ Conditions générales de vente</p>}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isRevision ? 'text-amber-600' : 'text-purple-600'}`}>
            <FileText className="w-5 h-5" />
            {isRevision ? 'Réviser le Devis' : 'Créer un Devis Détaillé'}
          </DialogTitle>
          <DialogDescription>
            Opération: {operation?.operationNumber || 'N/A'}
            {isRevision && operation?.estimatedPrice && (
              <span className="ml-2 text-amber-600">
                (Ancien montant: {operation.estimatedPrice.toLocaleString('fr-FR')} XOF)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step === currentStep ? 'bg-purple-600 text-white' :
                step < currentStep ? 'bg-green-600 text-white' :
                'bg-gray-300 text-gray-600'
              }`}>
                {step}
              </div>
              {step < totalSteps && (
                <div className={`flex-1 h-1 mx-2 ${
                  step < currentStep ? 'bg-green-600' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Labels */}
        <div className="flex justify-between text-xs text-gray-600 mb-4">
          <span className={currentStep === 1 ? 'font-semibold text-purple-600' : ''}>Items</span>
          <span className={currentStep === 2 ? 'font-semibold text-purple-600' : ''}>Assurance & Taxes</span>
          <span className={currentStep === 3 ? 'font-semibold text-purple-600' : ''}>Conditions</span>
          <span className={currentStep === 4 ? 'font-semibold text-purple-600' : ''}>Récapitulatif</span>
        </div>

        {/* Step Content */}
        <div className="py-4">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? onClose : handlePrevious}
            disabled={loading}
          >
            {currentStep === 1 ? 'Annuler' : <><ChevronLeft className="w-4 h-4 mr-1" /> Précédent</>}
          </Button>

          {currentStep < totalSteps ? (
            <Button onClick={handleNext} className="bg-purple-600 hover:bg-purple-700">
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Créer le Devis
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDetailedQuoteModal;
